<template>
    <app-layout :racingstars="$page.props.racingstars" :jetstream="$page.props.jetstream" :user="$page.props.user" title="Results">
        <template #header>
            Results
        </template>
        <div class="py-4 px-6 grid grid-cols-1 md:grid-cols-6">
            <div class="col-span-1 md:col-start-6 md:col-end-6">
                <jet-date-picker id="date" v-model="resultModel" :as-single="true" placeholder="Result Date" :formatter="format" />
            </div>
        </div>
        <div class="sm:px-6" v-if="meeting">
            <tw-card v-for="(race, raceKey) in meeting.races" :key="raceKey" :title="'Race '+race.number" class="sm:mb-6" height="dark:bg-gray-700 dark:sm:bg-gray-800">
                <div class="transition duration-500 ease-in-out dark:text-white p-6 sm:px-0 text-center sm:text-left">
                    {{ moment(race.starts_at).format('kk:mm') }} {{ meeting.name }}
                </div>

                <div class="shadow border-b border-gray-200 transition duration-500 ease-in-out dark:border-gray-800 sm:rounded-lg sm:mt-4" v-if="race.results.length">
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50 bg-gray-50 transition duration-500 ease-in-out dark:bg-gray-900">
                            <tr>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider transition duration-500 ease-in-out dark:text-white">
                                    Position
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider transition duration-500 ease-in-out dark:text-white">
                                    Horse
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider transition duration-500 ease-in-out dark:text-white hidden sm:table-cell">
                                    Jockey
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider transition duration-500 ease-in-out dark:text-white">
                                    Points
                                </th>
                                <th scope="col" class="relative px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-right transition duration-500 ease-in-out dark:text-white hidden sm:table-cell">
                                    Odds
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-300">
                            <tr v-for="(result, resultKey) in race.results" :key="resultKey" class="dark:hover:bg-opacity-80 dark:bg-gray-700  dark:sm:bg-gray-800 bg-white text-xs md:text-base">
                                <td class="px-6 py-4 whitespace-nowrap transition duration-500 ease-in-out dark:text-white truncate" :class="{ 'dark:bg-gray-900': hasRunner(result.runner) }">
                                    <div class="text-sm text-gray-900 transition duration-500 ease-in-out dark:text-white">
                                        <RacingStarsIcon v-if="hasRunner(result.runner)" class="inline-block h-6 w-6 text-green-500 transition duration-500 ease-in-out dark:text-racing-stars-yellow mr-2 truncate" /> {{ result.race_outcome_description }}
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap transition duration-500 ease-in-out dark:text-white" :class="{ 'dark:bg-gray-900': hasRunner(result.runner) }">
                                    <div class="flex items-center truncate">
                                        <div class="shrink-0 h-10 w-10">
                                            <div class="shrink-0 mr-4 rounded-full bg-white p-2 w-10 h-10 flex items-center justify-center">
                                                <img class="w-10" :src="result.runner.silk_image_path" alt="" />
                                            </div>
                                        </div>
                                        <div class="ml-4">
                                            <div class="text-sm font-medium text-gray-900 transition duration-500 ease-in-out dark:text-white truncate">
                                                {{ result.runner.horse.name }}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap transition duration-500 ease-in-out dark:text-white hidden sm:table-cell" :class="{ 'dark:bg-gray-900': hasRunner(result.runner) }">
                                    <div class="text-sm text-gray-900 transition duration-500 ease-in-out dark:text-white">{{ result.runner.jockey ? result.runner.jockey.name : 'Reserve' }}</div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap transition duration-500 ease-in-out dark:text-white" :class="{ 'dark:bg-gray-900': hasRunner(result.runner) }">
                                    <div class="text-sm text-gray-900 transition duration-500 ease-in-out dark:text-white">{{ result.points }}</div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium transition duration-500 ease-in-out dark:text-white hidden sm:table-cell" :class="{ 'dark:bg-gray-900': hasRunner(result.runner) }">
                                    {{ result.odds_description }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div v-else>
                    <alert type="info" class="mt-0">
                        <template #title>
                            Waiting for Race Results!
                        </template>
                        <template #message>
                            There currently no results for this race. Please check back later.
                        </template>
                    </alert>
                </div>
            </tw-card>
        </div>
        <div v-else class="py-4 px-6">
            <alert type="info">
                <template #title>
                    No Meeting Available!
                </template>
                <template #message>
                    There is currently no live meeting available. Please check back later.
                </template>
            </alert>
        </div>
    </app-layout>
</template>
<script>
import { ref, watch, getCurrentInstance, onMounted } from 'vue'
import AppLayout from '@/Layouts/Layout'
import moment from 'moment'
import JetDatePicker from '@/Jetstream/DatePicker.vue'
import { pickBy, throttle } from 'lodash';
import Alert from '@/Global/Alert.vue'
import RacingStarsIcon from '@/Global/RacingStarsIcon'
import { Inertia } from '@inertiajs/inertia'

export default {
    props: {
        date: String,
        meeting: Object,
        runners: Object,
    },

    setup(props) {

        const resultModel = ref(props.date)

        const format = ref({
            date: 'DD/MM/YYYY',
            month: 'MMM'
        })

        const hasRunner = (runner) => {
            return props.runners.some(e => e.id === runner.id)
        }

        const getNumberWithOrdinal = (n) => {
            var s = ['th', 'st', 'nd', 'rd'],
                v = n % 100;

            return n + (s[(v - 20) % 10] || s[v] || s[0]);
        }

        onMounted(() => {
            Echo.channel('results')
                .listen('RaceResultsUpdated', (e) => {
                    if (route().current() === 'results') {
                        Inertia.reload()
                    }
                })
        })

        watch(resultModel, (currentValue, oldValue) => {
            let params = pickBy({
                date: currentValue
            })

            Inertia.post(route('results'), params)
        })

        return {
            format,
            resultModel,
            moment,
            getNumberWithOrdinal,
            hasRunner,
        }

    },

    components: {
        AppLayout,
        JetDatePicker,
        RacingStarsIcon,
    },
}

</script>
